import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import React from 'react';
import { Helmet } from 'react-helmet-async';

const processSteps = [
    {
      title: '1. Discovery',
      description: 'The journey begins with a deep dive into your business. We conduct thorough consultations to understand your vision, goals, challenges, and the specific requirements of your project. This foundational step ensures our solution is perfectly aligned with your business objectives.',
      icon: '🔍',
      details: [
        'Initial consultation and stakeholder interviews.',
        'Analysis of existing systems and workflows.',
        'Definition of project scope and key performance indicators (KPIs).',
      ],
    },
    {
      title: '2. Strategy',
      description: 'With a clear understanding of your needs, we architect a tailored solution. Our team develops a comprehensive strategy that includes technology stack selection, system architecture design, and a detailed project roadmap. This blueprint guides the development process and ensures a successful outcome.',
      icon: '📋',
      details: [
        'Solution architecture and technical design.',
        'Project planning, timeline, and milestone definition.',
        'User experience (UX) and user interface (UI) design mockups.',
      ],
    },
    {
      title: '3. Development',
      description: 'This is where your vision comes to life. Following agile methodologies, we build your solution in iterative sprints. This approach allows for flexibility, continuous feedback, and ensures the final product is robust, scalable, and precisely meets your specifications.',
      icon: '⚡',
      details: [
        'Agile development sprints with regular progress reviews.',
        'Continuous integration and continuous deployment (CI/CD) practices.',
        'Transparent communication and collaboration throughout.',
      ],
    },
    {
      title: '4. Launch',
      description: 'Before deployment, we conduct rigorous testing to ensure your solution is flawless. Our quality assurance team performs comprehensive checks for functionality, performance, and security. A smooth and seamless deployment process guarantees your solution is ready for success from day one.',
      icon: '🚀',
      details: [
        'End-to-end quality assurance (QA) and user acceptance testing (UAT).',
        'Performance, security, and scalability testing.',
        'Data migration and seamless deployment.',
      ],
    },
    {
      title: '5. Manage',
      description: "Our partnership doesn't end at launch. We stand by you, offering continuous monitoring, proactive maintenance, and ongoing support to ensure your solution operates at peak performance. We handle the technical details so you can focus on your business.",
      icon: '🤖',
      details: [
        '24/7 infrastructure monitoring and alerts.',
        'Regular security patches and software updates.',
        'Ongoing feature enhancements and technical support.',
      ],
    },
];

const ProcessPage = () => {
    return (
        <>
            <Helmet>
                <title>Our Process | nautdigital</title>
                <meta name="description" content="Discover our transparent, collaborative process for delivering successful digital projects." />
            </Helmet>
            <div className="relative min-h-screen overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-dark-lighter to-black pointer-events-none z-0" />
                <div className="container mx-auto pt-32 pb-16 px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-20"
                    >
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                            Our Collaborative Process
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            We follow a proven, transparent process to ensure your project's success from initial concept to final deployment and beyond.
                        </p>
                    </motion.div>

                    <div className="space-y-24">
                        {processSteps.map((step, index) => (
                            <React.Fragment key={step.title}>
                                <motion.div
                                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.6 }}
                                    className={`flex flex-col md:flex-row items-center gap-12 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}
                                >
                                    <div className="flex-1 text-center md:text-left">
                                        <h2 className="text-3xl font-bold text-primary mb-4">{step.title}</h2>
                                        <p className="text-lg text-gray-300 mb-6">{step.description}</p>
                                        <ul className="space-y-3">
                                            {step.details.map((detail, i) => (
                                                <li key={i} className="flex items-start">
                                                    <span className="text-accent mr-3 mt-1">✓</span>
                                                    <span className="text-gray-400">{detail}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="flex-1 flex justify-center">
                                        <div className="relative bg-dark-lighter border-2 border-accent/50 rounded-full w-64 h-64 flex items-center justify-center text-8xl">
                                            {step.icon}
                                        </div>
                                    </div>
                                </motion.div>
                                {/* CTA after Strategy section (index 1) */}
                                {index === 1 && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6 }}
                                        className="relative text-center bg-dark-lighter p-12 rounded-2xl border border-accent/20 shadow-lg overflow-hidden"
                                    >
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-accent/30 rounded-full blur-2xl opacity-70 -mt-10 -mr-10 pointer-events-none" />
                                        <h3 className="relative z-10 text-3xl font-bold text-white mb-4">Ready to understand your strategy?</h3>
                                        <p className="relative z-10 text-lg text-gray-300 max-w-2xl mx-auto mb-8">
                                            A solid strategy is the foundation of every successful project. Let's talk about yours.
                                        </p>
                                        <Link
                                            to="/contact?message=Hi%2C%0AI'm%20interested%20in%20discussing%20a%20bespoke%20strategy%20to%20solve%20a%20business%20problem%20I%20have.#contact-form"
                                            className="relative z-10 inline-block px-8 py-4 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-all duration-200"
                                        >
                                            Book a Free Consultation
                                        </Link>
                                    </motion.div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProcessPage; 